/**
 * InlineLabelSwitch - Switch with text labels inside
 *
 * A switch component with text labels displayed inside the switch background
 * (similar to the old PlainToggleSwitch). The labels appear on either side of
 * the thumb and are visible based on the checked state.
 *
 * Example: [Live | ●] or [● | Draft]
 */

import * as React from "react"
import * as SwitchPrimitive from "@radix-ui/react-switch"
import { cn } from "@shadcn/lib/utils"

export interface InlineLabelSwitchProps extends React.ComponentProps<typeof SwitchPrimitive.Root> {
	/**
	 * Label to show when switch is checked (on) - appears on the left
	 * @default "On"
	 */
	onLabel?: string
	/**
	 * Label to show when switch is unchecked (off) - appears on the right
	 * @default "Off"
	 */
	offLabel?: string
}

export function InlineLabelSwitch({ className, onLabel = "On", offLabel = "Off", ...props }: InlineLabelSwitchProps) {
	return (
		<SwitchPrimitive.Root
			data-slot="inline-label-switch"
			className={cn(
				"peer relative inline-flex h-[22px] w-[50px] shrink-0 items-center rounded-full border border-transparent shadow-xs transition-all outline-none",
				"focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
				"disabled:cursor-not-allowed disabled:opacity-50",
				"data-[state=checked]:bg-primary data-[state=unchecked]:bg-input dark:data-[state=unchecked]:bg-input/80",
				className
			)}
			{...props}>
			{/* Left label (shown when checked) */}
			<span
				className={cn(
					"absolute left-[5px] text-[9px] font-normal leading-[22px] text-primary-foreground transition-opacity pointer-events-none select-none",
					"data-[state=checked]:opacity-100 data-[state=unchecked]:opacity-0"
				)}
				data-state={props.checked ? "checked" : "unchecked"}>
				{onLabel}
			</span>

			{/* Right label (shown when unchecked) */}
			<span
				className={cn(
					"absolute right-[5px] text-[9px] font-normal leading-[22px] text-foreground transition-opacity pointer-events-none select-none",
					"data-[state=checked]:opacity-0 data-[state=unchecked]:opacity-100"
				)}
				data-state={props.checked ? "checked" : "unchecked"}>
				{offLabel}
			</span>

			{/* Thumb */}
			<SwitchPrimitive.Thumb
				data-slot="switch-thumb"
				className={cn(
					"pointer-events-none block h-[18px] w-[18px] rounded-full ring-0 transition-transform shadow-sm m-[1px]",
					"bg-background dark:data-[state=unchecked]:bg-foreground dark:data-[state=checked]:bg-primary-foreground",
					"data-[state=checked]:translate-x-[28px] data-[state=unchecked]:translate-x-[0px]"
				)}
			/>
		</SwitchPrimitive.Root>
	)
}
